import React, {useContext, useState, useEffect, useCallback} from "react";
import PropTypes from "prop-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExport, faSearch, faFilter} from "@fortawesome/free-solid-svg-icons";
import styled, {css} from "styled-components";

// Contexts
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Components
import BaseTable from "../../components/BaseTable.js";
import Pagination from "../../components/Pagination.js";
import Dropdown from "../../components/Dropdown.js";
import PhoneNumbers from "../../components/PhoneNumbers.js";
import Badge from "../../components/Badge.js";
import ModalExport from "../../components/ModalExport.js";
import ModalUserFilter from "../general/ModalUserFilter.js";
import ModalViewAllFacilityUsers from "../general/ModalViewAllFacilityUsers.js";

// Style
import {bp} from "../../style/components/breakpoints.js";
import {colors, heroTheme, pad} from "../../style/components/variables.js";
import {
  Button,
  Heading,
  Inline,
  TableWrapper,
  TableHeaderWrap,
  TableMenu,
  TableFooter,
  Text,
  Search,
  SearchWrapper,
  SearchIcon,
  Abbr,
  Small,
  Pill
} from "../../style/components/general.js";

const formatUsers = formattedUsers =>
  formattedUsers.map(user => {
    const {
      publicId,
      email,
      firstName,
      lastName,
      type,
      role,
      phones,
      primary,
      company,
      isInvited,
      isAccepted,
      isDeleted
    } = user;

    return {
      publicId: publicId,
      fullName: `${firstName} ${lastName}`,
      userCompanyId: company?.name,
      userRoleId: role?.label || type?.name?.toUpperCase(),
      status: (
        <>
          {isInvited && isAccepted && !isDeleted && (
            <Pill color={heroTheme.success}>
              <Small inverted>Active</Small>
            </Pill>
          )}
          {isDeleted && (
            <Pill color={colors.gray}>
              <Small inverted>
                <Abbr title="User is deleted">deleted!</Abbr>
              </Small>
            </Pill>
          )}
          {isInvited && !isAccepted && !isDeleted && (
            <Pill color={heroTheme.warning}>
              <Small inverted>
                <Abbr title="User has not accepted invitation">invited</Abbr>
              </Small>
            </Pill>
          )}
          {!isInvited && !isDeleted && (
            <Pill color={colors.gray}>
              <Small inverted>
                <Abbr title="User has not been invited">contact</Abbr>
              </Small>
            </Pill>
          )}
        </>
      ),
      email: <a href={`mailto:${email}`}>{email}</a>,
      phone: primary && <PhoneNumbers primary={primary} phones={phones} />,
      isDeleted
    };
  });

const TableFacilityUsers = ({facilityId}) => {
  const isMounted = useMountedState();

  const {roles, atLeast, roleCanAccessResource} = useContext(AuthContext);

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [users, setUsers] = useState();
  // Search
  const [query, setQuery] = useState("");
  // Filters
  const [activeFilters, setActiveFilters] = useState();
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(1);
  const [orderBy, setOrderBy] = useState("asc");
  const [groupBy, setGroupBy] = useState("fullName");
  const [limits, setLimits] = useState([]);
  const [limit, setLimit] = useState(10);
  // Modals
  const [showModalFilters, setShowModalFilters] = useState(false);
  const [showModalExport, setShowModalExport] = useState(false);
  const [showModalViewAll, setShowModalViewAll] = useState(false);

  const {api: apiFacilityUsers} = useApi("facility-users");

  const getFacilityUsers = useCallback(
    p => {
      setLoadingUsers(true);

      const filter = {
        ExcludeCreator: true,
        ExcludeContact: true
      };

      if (query) filter.Search = query;
      if (activeFilters?.roles) filter.Roles = activeFilters.roles;

      apiFacilityUsers
        .callGet(null, {
          facilityId: facilityId,
          page: p || 1,
          orderBy,
          groupBy,
          limit,
          filter: JSON.stringify(filter)
        })
        .then(({status, data}) => {
          if (status === 200 && data) {
            setCurrentPage(data.page);
            setPageTotal(data.pages);
            const formatted = formatUsers(data.users);
            setUsers(formatted);

            // Page Limit
            const {total: dataTotal} = data;
            setTotal(dataTotal);
            let count = 10;
            const temp = [];
            while (count < dataTotal + 10 && count <= 30) {
              temp.push(count);
              count += 10;
            }

            setLimits(temp);
            setLoadingUsers(false);
          }
        });
    },
    [apiFacilityUsers, facilityId, orderBy, groupBy, limit, query, activeFilters]
  );

  // Initial Load
  useEffect(() => {
    if (isMounted() && facilityId && !users) getFacilityUsers();
  }, [isMounted, getFacilityUsers, facilityId, users]);

  // Update users on search, filter, sort or limit
  useEffect(() => {
    if (isMounted()) {
      setUsers(undefined);
      setCurrentPage(1);
    }
  }, [isMounted, query, activeFilters, orderBy, groupBy, limit]);

  const handleSearch = e => {
    setCurrentPage(1);
    setQuery(e.target.value);
  };

  return (
    <>
      <TableWrapper>
        <TableHeaderWrap>
          <StyledInline>
            <Heading>Users</Heading>
            <InlineSpaced>
              <SearchWrapper>
                <SearchIcon>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchIcon>
                <Search
                  name="search"
                  type="text"
                  placeholder="Search..."
                  onChange={e => handleSearch(e)}
                />
              </SearchWrapper>
              <TableMenu>
                {atLeast("admin") && roles?.length > 0 && (
                  <Button type="button" onClick={() => setShowModalFilters(!showModalFilters)}>
                    <Badge
                      count={activeFilters?.roles ? activeFilters.roles.length : 0}
                      title="Active filters"
                      offset="14px"
                      color={colors.heroGreen}
                    />
                    <FontAwesomeIcon icon={faFilter} />
                  </Button>
                )}
                {roleCanAccessResource("user", "export") && users && users.length > 0 && (
                  <Option
                    type="button"
                    onClick={() => {
                      setShowModalExport(true);
                    }}>
                    <abbr title="Export">
                      <FontAwesomeIcon icon={faFileExport} />
                    </abbr>
                  </Option>
                )}
              </TableMenu>
            </InlineSpaced>
          </StyledInline>
        </TableHeaderWrap>

        <BaseTable
          headings={{
            publicId: {header: "", disabled: true},
            fullName: {header: "Name", disabled: false},
            userCompanyId: {header: "Company", disabled: false},
            userRoleId: {header: "Role", disabled: false},
            status: {header: "Status", disabled: false},
            email: {header: "Email", disabled: false},
            phone: {header: "Phone", disabled: true}
          }}
          data={users || null}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          loading={loadingUsers}
        />
        <TableFooter>
          {total > 0 && (
            <>
              <Inline>
                {total > 10 && limits.length > 0 && (
                  <PageLimit>
                    <Dropdown
                      options={limits}
                      selection={limit}
                      setSelection={selection => {
                        setLimit(selection);
                        setCurrentPage(1);
                      }}
                    />
                  </PageLimit>
                )}
                <Text quiet>
                  {limit <= 30 && pageTotal > 1 && limits.length > 0 && "per page, "}
                  <ModalViewLink
                    disabled={!total || total <= 10}
                    onClick={!total || total <= 10 ? undefined : () => setShowModalViewAll(true)}>
                    {total} total
                  </ModalViewLink>
                </Text>
              </Inline>
              {limit <= 30 && pageTotal > 1 && (
                <Pagination
                  current={currentPage}
                  setCurrent={setCurrentPage}
                  pageTotal={pageTotal}
                  updateData={() => getFacilityUsers(currentPage)}
                  loading={loadingUsers}
                />
              )}
            </>
          )}
        </TableFooter>
      </TableWrapper>

      {showModalFilters && (
        <ModalUserFilter
          visible={showModalFilters}
          setVisible={setShowModalFilters}
          filters={activeFilters}
          setFilters={setActiveFilters}
        />
      )}

      {showModalExport && (
        <ModalExport
          visible={showModalExport}
          setVisible={setShowModalExport}
          allowRange={false}
          getExport={async exportParams =>
            apiFacilityUsers.callGet(null, {
              ...exportParams,
              facilityId: facilityId,
              filter: JSON.stringify({ExcludeCreator: true})
            })
          }
        />
      )}

      {showModalViewAll && (
        <ModalViewAllFacilityUsers visible={showModalViewAll} setVisible={setShowModalViewAll} />
      )}
    </>
  );
};

TableFacilityUsers.propTypes = {
  facilityId: PropTypes.number.isRequired
};

// Style Overrides
const StyledInline = styled(Inline)`
  gap: ${pad}px;
  display: block;
  width: 100%;

  ${bp(1)} {
    display: flex;
  }
`;

const Option = styled(Button)`
  margin-left: ${pad}px;
`;

const PageLimit = styled.div`
  margin-right: ${pad}px;

  select {
    padding: ${pad / 2}px;
  }
`;

const InlineSpaced = styled(Inline)`
  justify-content: space-between;
  width: 100%;
`;

const ModalViewLink = styled.span`
  ${({disabled}) =>
    !disabled &&
    css`
      text-decoration: underline;
      cursor: pointer;
    `}
`;

export default TableFacilityUsers;
